import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardBody,
  Table,
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  CardTitle,
  Form,
  Input,
  FormGroup,
  UncontrolledButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import { PersonPlus, ChatRightQuote, Link45deg, Envelope } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import qs from 'qs';
import SweetAlerts from '../../components/SweetAlerts';
import Product from '../../components/Product';
import SimpleHeader from '../../components/Headers/SimpleHeader';
import SalesInput from '../../components/SalesInput';
import client from '../../utils/client';

import 'react-datepicker/dist/react-datepicker.css';
import { useCampaign, useProducts, useStockBalance } from '../../utils/api/react-query-api-client';
import { isSuperAdmin } from '../../utils/helpers';

const superAdmin = isSuperAdmin();

let totalQty = 0;
let totalCardQty = 0;

const oneToFifty = Array.from({ length: 50 }, (_, i) => i + 1);

const Sales = () => {
  const { t } = useTranslation();

  const [inProgress, setInProgress] = useState(null);
  const [error, setError] = useState(null);
  const [productsQty, setProductsQty] = useState({});
  const [usersData, setUsersData] = useState(null);
  const [usersDataValid, setUsersDataValid] = useState(false);
  const [salesRegistered, setSalesRegistered] = useState(null);
  const [salesData, setSalesData] = useState({ data: [], page: -1 });
  const [searchData, setSearchData] = useState({});
  const [sale, setSale] = useState(null);

  const campaign = useCampaign();
  const { data: campaignData } = campaign;

  const products = useProducts();
  const { data: productsData } = products;

  const stockBalance = useStockBalance();
  const { data: stockBalanceData } = stockBalance;

  const location = useLocation();

  const ref = useRef(null);

  useEffect(() => {
    document.body.classList.add('sales');
    window.scrollTo(0, 0);

    return function cleanup() {
      document.body.classList.remove('sales');
    };
  }, []);

  useEffect(() => {
    const params = qs.parse(location.search, {
      ignoreQueryPrefix: true
    });

    if (params.action === 'search') {
      setSearchData({ cardNumber: params.cardNumber, email: params.email, action: 'search' });
    }
  }, []);

  useEffect(() => {
    if (searchData?.action === 'search') {
      loadSales('search');
      ref.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [searchData?.action]);

  const onProductChange = (subProductId, qty) => {
    let newQty = totalCardQty > 1 ? qty * (campaignData?.minCardsRequirment || 1) : qty;
    newQty = (productsQty[subProductId] || 0) + newQty;
    newQty = newQty < 0 ? 0 : newQty;

    const updatedProductsQty = {
      ...productsQty,
      [subProductId]: newQty
    };

    totalQty = 0;
    totalCardQty = 0;
    Object.keys(updatedProductsQty).forEach((pId) => {
      const pQty = updatedProductsQty[pId];
      // All products including PLUS
      totalQty += pQty;

      pId = parseInt(pId, 10);
      // Only cards, excluding PLUS
      const product = productsData.find((p) => p.id === pId);
      totalCardQty += product?.code === 'GHPL' ? 0 : pQty;
    });

    setProductsQty(updatedProductsQty);
  };

  const proceedWithRegistration = () => {
    const users = [];
    productsData.forEach((p) =>
      p.subProducts
        .filter((sp) => productsQty[sp.id] > 0)
        .forEach((sp) => {
          users.push(
            [...Array(productsQty[sp.id]).keys()].map((u) => ({
              subProductId: sp.id,
              name: `${p.name} - ${sp.name}`,
              id: u,
              digital: sp.digital,
              email: '',
              cardNumber: ''
            }))
          );
        })
    );

    setUsersData(users.flat());
  };

  const onUserChange = ({ name, value }, subProductId, userId) => {
    const updatedUserData = usersData.map((u) =>
      u.subProductId === subProductId && u.id === userId ? { ...u, [name]: value } : { ...u }
    );
    const invalidData = updatedUserData.filter(
      (u) => !u.email || (!u.digital && !u.cardNumber)
    ).length;

    setUsersDataValid(!invalidData);
    setUsersData(updatedUserData);
  };

  const registerData = async () => {
    setInProgress('register');
    setError(null);

    const data = usersData.map((u) => ({ ...u, productId: u.subProductId }));
    const { data: sData, error: err } = await client(`admin-gc/sales?super-admin=${superAdmin}`, {
      body: { data, campaign: campaignData?.code }
    });

    setInProgress(null);
    if (err) {
      setError(err.message);
    } else {
      setSalesRegistered(sData);
    }
  };

  const clearAll = () => {
    totalQty = 0;
    totalCardQty = 0;
    ReactDOM.unstable_batchedUpdates(() => {
      setProductsQty({});
      setUsersData(null);
      setSalesRegistered(null);
      // setShowDetailedData(null);
      setUsersDataValid(false);
      setError(null);
    });
  };

  const loadSales = async (type) => {
    let nextPage = salesData.page;

    setInProgress(type);

    if (type === 'search') {
      nextPage = 0;
    } else if (type === 'load') {
      nextPage = salesData.page + 1;
    }

    let queryParams = `?page=${nextPage}`;
    if (searchData.email) {
      queryParams += `&email=${searchData.email}`;
    }

    if (searchData.cardNumber) {
      queryParams += `&cardNumber=${searchData.cardNumber}`;
    }

    const { data: sData } = await client(`admin-gc/sales${queryParams}`);

    if (type === 'search' || type === 'reload') {
      setSalesData({ data: sData, page: nextPage, noMoreData: sData?.length === 0 });
    } else {
      setSalesData({
        data: [...salesData.data, ...sData],
        page: nextPage,
        noMoreData: sData?.length === 0
      });
    }

    setInProgress(null);
  };

  const searchFieldUpdated = ({ target }) => {
    const { name, value } = target;
    setSearchData({
      ...searchData,
      [name]: value
    });
  };

  const handleCardSale = async (type, s) => {
    setInProgress(type);
    setSale({ ...sale, success: false, error: false });
    if (type === 'cancel') {
      const { error: saleError, data: sl } = await client('admin-gc/sales', { body: s }, 'DELETE');
      const newSale = sl || sale;
      setSale({ ...newSale, success: !saleError && 'deleted', error: saleError });
      if (!saleError) {
        loadSales('reload');
      }
    } else if (type === 'free') {
      const { error: saleError, data: sl } = await client(
        'admin-gc/sales',
        { body: { ...s, free: 1 } },
        'PUT'
      );
      const newSale = sl || sale;
      setSale({ ...newSale, success: !saleError && 'free', error: saleError });
      if (!saleError) {
        loadSales('reload');
      }
    } else {
      const { error: saleError } = await client('admin-gc/sales/resend', {
        body: { sale: s, type }
      });
      setSale({ ...sale, success: !saleError && 'resent', error: saleError });
    }
    setInProgress(null);
  };

  const removeFromStockBalance = async (qty) => {
    setError(null);
    setInProgress('remove');
    const { error: sbError } = await client(
      `admin-gc/sales/stock-balance`,
      { body: { qty: parseInt(qty, 10) } },
      'DELETE'
    );

    setInProgress(null);
    if (sbError) {
      setError(sbError.message);
    } else {
      stockBalance.refetch();
    }
  };

  // console.log('stockBalance', stockBalance, stockBalanceData);
  // console.log('productsData', productsData);
  // console.log('sale', sale);
  // console.log('searchData', searchData);
  // console.log('salesData', salesData);

  const isCardSale = sale?.product !== 'GHPL';

  return (
    <>
      <SimpleHeader name={t('sales.title')} />
      <Container className="mt--6" fluid>
        <Row className="flex-column-reverse flex-lg-row">
          <Col lg={campaignData ? '8' : '12'}>
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">{t('sales.title')}</h3>
                </CardHeader>
                <CardBody>
                  {stockBalanceData &&
                    t('sales.stock-balance', {
                      balance: stockBalanceData.balance,
                      ordered: stockBalanceData.ordered
                    })}
                  {superAdmin && (
                    <div>
                      <UncontrolledButtonDropdown disabled={!!inProgress} className="my-3">
                        <DropdownToggle caret color="primary">
                          {inProgress && <Spinner size="sm" />}
                          Remove from stock balance
                        </DropdownToggle>
                        <DropdownMenu onClick={(e) => removeFromStockBalance(e.target.innerText)}>
                          <DropdownItem>
                            <span className="small">
                              Proceed with caution. Once you click on a number that quantity is
                              <br />
                              removed from the stock balance, it cannot be undone.
                            </span>
                          </DropdownItem>
                          <DropdownItem divider />
                          {oneToFifty.map((n) => (
                            <DropdownItem key={n}>{n}</DropdownItem>
                          ))}
                        </DropdownMenu>
                      </UncontrolledButtonDropdown>
                      {error && <Alert color="danger">{error}</Alert>}
                    </div>
                  )}
                  {campaignData?.minCardsRequirment > 1 && (
                    <p className="alert alert-secondary font-weight-bold">
                      {t('sales.campaign-info-2-cards', {
                        minCards: campaignData?.minCardsRequirment
                      })}
                    </p>
                  )}

                  {products.isFetching && <Spinner size="md" />}
                  {productsData?.map((p) => (
                    <Product
                      key={p.id}
                      data={p}
                      qty={productsQty}
                      onChange={onProductChange}
                      digitalCardsAvailable={stockBalanceData?.balance > 0}
                    />
                  ))}

                  {productsData && (
                    <div className="mt-3 float-right text-align-right">
                      {campaignData && (
                        <>
                          {totalCardQty > 0 && totalCardQty < campaignData.minCardsRequirment && (
                            <p className="alert alert-default">
                              {t('sales.campaign-requires-2-cards', {
                                minCards: campaignData?.minCardsRequirment
                              })}
                            </p>
                          )}
                        </>
                      )}
                      {totalQty === 0 && <p className="mb-0">{t('sales.select-numer')}</p>}
                      <Button
                        className="btn btn-success px-5"
                        onClick={proceedWithRegistration}
                        disabled={totalQty === 0}
                      >
                        <span>{t('default.next')}</span>
                      </Button>
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>
          </Col>
          <Col lg="4">
            <div className="card-wrapper">
              {campaignData && (
                <Card>
                  <CardBody>
                    <div className="icon icon-shape bg-green text-white rounded-circle shadow">
                      <i className="ni ni-active-40" />
                    </div>
                    <CardTitle className="d-inline ml-2" tag="h2">
                      {t('default.ongoing-campaign')}
                    </CardTitle>
                    <br />
                    <Link to="/golfclub-admin/material">
                      <Link45deg />
                      {t('default.campaign-material', 'Kampanjmaterial')}
                    </Link>
                    <h3 className="mt-3">{campaignData.name}</h3>
                    <p>
                      {t('sales.campaign-info', {
                        campaignStart: campaignData.validFrom,
                        campaignEnd: campaignData.validTo,
                        campaign: campaignData.name
                      })}
                    </p>
                    <h3>{t('sales.campaign-info-title')}</h3>
                    <p
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: campaignData.description }}
                    />
                  </CardBody>
                </Card>
              )}
            </div>
          </Col>
        </Row>

        <Row>
          <Col>
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">{t('sales.events', 'Händelser')}</h3>
                </CardHeader>
                <CardBody>
                  <div ref={ref} />
                  <Row className="mb-3">
                    <Col>
                      <Input
                        type="text"
                        placeholder={t('sales.events-search-on-card-nr', 'Sök på kortnummer')}
                        name="cardNumber"
                        value={searchData.cardNumber || ''}
                        onChange={searchFieldUpdated}
                        disabled={false}
                      />
                    </Col>
                    <Col>
                      <Input
                        type="text"
                        placeholder={t('sales.events-search-on-email', 'Sök på e-post')}
                        name="email"
                        value={searchData.email || ''}
                        onChange={searchFieldUpdated}
                        disabled={false}
                      />
                    </Col>
                    <Col>
                      <Button onClick={() => loadSales('search')}>
                        {t('default.search', 'Sök')}
                      </Button>
                    </Col>
                  </Row>
                  <Table responsive>
                    <thead className="thead-light">
                      <tr>
                        <th>{t('default.date')}</th>
                        <th>{t('default.type', 'Typ')}</th>
                        <th>{t('default.card-number')}</th>
                        <th>{t('default.email')}</th>
                        <th>{t('default.product')}</th>
                        <th>{t('default.campaign', 'Kampanj')}</th>
                        {/* <th>{t('order.a-price')}</th> */}
                        <th>{`${t('invoices.invoice')} ${t('default.date')}`}</th>
                        <th>{t('invoices.invoice')} #</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesData.data?.map((s) => (
                        <tr key={s.id} className={(s.canceled || s.credited) && 'canceled'}>
                          <td>{s.date}</td>
                          <td>{s.fromSaleId ? 'web' : 'club'}</td>
                          <td>
                            {!s.canceled && !s.credited ? (
                              <Button color="link" className="p-0" onClick={() => setSale(s)}>
                                {s.cardNr}
                              </Button>
                            ) : (
                              s.cardNr
                            )}
                          </td>
                          <td>{s.email}</td>
                          <td>{s.product}</td>
                          <td>
                            {!s.canceled && !s.credited && s.campaign ? (
                              <Button color="link" className="p-0" onClick={() => setSale(s)}>
                                {s.campaign}
                              </Button>
                            ) : (
                              s.campaign
                            )}
                          </td>
                          {/* <td className={s.free && 'canceled'}>{s.price}</td> */}
                          <td>{s.invoiceCreated}</td>
                          <td>{s.invoiceNr}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <div className="text-center mt-3">
                    {!salesData.noMoreData && (
                      <Button onClick={() => loadSales('load')} disabled={inProgress === 'load'}>
                        {inProgress === 'load' && <Spinner size="sm" className="mr-2" />}
                        {salesData.page === -1
                          ? t('sales.load-events', 'Ladda händelser')
                          : t('sales.loading-more', 'Laddar fler')}
                      </Button>
                    )}
                    {/* t('sales.load-no-more', 'Inga fler att ladda') */}
                  </div>
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>

      {usersData?.length > 0 && (
        <Modal isOpen centered size="lg">
          <ModalHeader>
            <PersonPlus className="mr-2" />
            {t('sales.register-sales')}
          </ModalHeader>
          <ModalBody>
            <div>
              <p>{t('sales.fill-in-information')}</p>
              {superAdmin && (
                <p className="alert alert-warning">
                  OBS! Notera att inloggad som super admin kommer vissa kontroller av försäljningen
                  inte genomföras. Säkerställ att det är vad du vill eller logga in som vanlig
                  användare
                </p>
              )}
              {campaignData && (
                <>
                  <p className="alert alert-default">
                    {totalCardQty < campaignData.minCardsRequirment ? (
                      <span>
                        {t('sales.campaign-requires-2-cards', {
                          minCards: campaignData.minCardsRequirment
                        })}
                      </span>
                    ) : (
                      <span>
                        {t('sales.campaign-registration', { campaign: campaignData.name })}
                      </span>
                    )}
                  </p>
                </>
              )}
            </div>
            <Form className="mt-4">
              {/* autoComplete="off" */}
              {usersData.map((u, idx) => (
                <div key={`${u.subProductId}-${u.id}`}>
                  {u.subProductId !== usersData[idx - 1]?.subProductId && (
                    <h5 className="mt-3 font-weight-bold">{u.name}</h5>
                  )}
                  <SalesInput data={u} onChange={onUserChange} />
                </div>
              ))}
            </Form>
            {error && <Alert color="danger">{error}</Alert>}
          </ModalBody>
          <ModalFooter>
            <Button
              color="success"
              onClick={registerData}
              disabled={!usersDataValid || inProgress === 'register'}
            >
              {inProgress === 'register' && <Spinner size="sm" />}
              {t('sales.register-sales')}
            </Button>
            <Button
              color="danger"
              onClick={() => {
                setError(null);
                setUsersData(null);
              }}
            >
              {t('default.cancel')}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {!!salesRegistered && (
        <SweetAlerts
          type="success"
          title={t('sales.registered', 'Försäljningen registrerad')}
          confirmBtnText={t('default.close')}
          onConfirm={clearAll}
          className="big"
        >
          {campaignData && salesRegistered[0].campaignCode && (
            <Alert color="default">
              <ChatRightQuote style={{ fontSize: '20px' }} className="mr-2" />
              {t('sales.sales-registered', { campaign: campaignData.name })}
            </Alert>
          )}

          <Table className="align-items-center mt-2" responsive>
            <thead className="thead-light">
              <tr>
                <th>{t('default.card-number')}</th>
                <th>{t('default.email')}</th>
                <th>{t('default.product')}</th>
              </tr>
            </thead>
            <tbody>
              {salesRegistered.map((u) => (
                <tr key={`${u.subProductId}-${u.id}`}>
                  <td>
                    <b>{u.cardNumber}</b>
                  </td>
                  <td>
                    <a href={`mailto: ${u.email}`}>{u.email}</a>
                  </td>
                  <td>{u.name}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </SweetAlerts>
      )}

      {sale && (
        <Modal isOpen centered size="lg">
          <ModalHeader className="pb-0">
            <Envelope className="mr-2" /> {t('sales.card-sales', 'Kortförsäljning')}
          </ModalHeader>
          <ModalBody className="pb-0">
            {isCardSale && (
              <FormGroup className="mb-0">
                {sale.campaign && <p>{t('sales.card-campaign', { campaign: sale.campaign })}</p>}
                {sale.digitalCard && (
                  <p className="mb-0">{t('sales.card-digital', 'Kortet är ett digitalt kort')}</p>
                )}
                <Input type="email" value={sale.cardNr} readOnly className="mb-3" />
                {sale.campaignDigitalCards?.length > 0 && (
                  <>
                    {sale.campaignDigitalCards.map((c) => (
                      <>
                        {sale.digitalCard && (
                          <p className="mb-0">
                            {t('sales.card-digital', 'Kortet är ett digitalt kort')}
                          </p>
                        )}
                        <Input type="email" value={c} readOnly className="mb-3" />
                      </>
                    ))}
                  </>
                )}
                <Input
                  type="email"
                  value={sale.email}
                  onChange={(e) => setSale({ ...sale, email: e.target.value })}
                />
              </FormGroup>
            )}
            {sale.success && (
              <Alert color="success" className="mt-2">
                {sale.success === 'resent' && t('default.mail-sent', 'Mailet har skickats')}
                {sale.success === 'deleted' &&
                  t('default.sale-canceled', 'Försäljningen har makulerats')}
                {sale.success === 'free' &&
                  t('default.marked-sale-free', 'Försäljningen är markerad som gratis')}
              </Alert>
            )}
            {sale.error && (
              <Alert color="danger" className="mt-2">
                {sale.error.message}
                {/* {t('default.mail-not-sent', 'Mailet kunde tyvärr inte skickas')} */}
              </Alert>
            )}
          </ModalBody>
          <ModalFooter className="pt-2">
            {!sale.canceled && (
              <>
                {isCardSale && (sale.digitalCard || sale.campaignDigitalCards?.length > 0) && (
                  <Button
                    color="success"
                    onClick={() => handleCardSale('card-number', sale)}
                    disabled={!!inProgress}
                  >
                    {t('sales.send-card-number', 'Skicka kortnummer')}
                    {inProgress === 'card-number' && <Spinner size="sm" className="ml-2" />}
                  </Button>
                )}
                {isCardSale && sale.campaign && sale.campaignMailSent && (
                  <Button
                    color="success"
                    onClick={() => handleCardSale('campaign', sale)}
                    disabled={!!inProgress}
                  >
                    {t('sales.send-campaign-mail', 'Skicka kampanj-mail')}
                    {inProgress === 'campaign' && <Spinner size="sm" className="ml-2" />}
                  </Button>
                )}
                {superAdmin && !sale.fromSaleId && (
                  <Button
                    color="danger"
                    onClick={() => handleCardSale('cancel', sale)}
                    disabled={!!inProgress}
                  >
                    {t('sales.cancel-sale', 'Makulera försäljningen')}
                    {inProgress === 'cancel' && <Spinner size="sm" className="ml-2" />}
                  </Button>
                )}
                {superAdmin && !sale.fromSaleId && !isCardSale && !sale.free && (
                  <Button
                    color="warning"
                    onClick={() => handleCardSale('free', sale)}
                    disabled={!!inProgress}
                  >
                    {t('sales.mark-sale-free', 'Markera som gratis')}
                    {inProgress === 'free' && <Spinner size="sm" className="ml-2" />}
                  </Button>
                )}
              </>
            )}
            <Button color="default" onClick={() => setSale(null)}>
              {t('default.close')}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default Sales;
